import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronRight, Calendar, Users, Sparkles, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { useCms } from "@/lib/cms";

const steps = ["Program", "Date", "School", "Teachers", "Review"];

export default function Book() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { site } = useCms();
  const whatsapp = site?.whatsapp || "";
  const [step, setStep] = useState(0);
  const [programs, setPrograms] = useState([]);
  const [data, setData] = useState({
    program_id: params.get("program") || "",
    preferred_date: "",
    school_name: "", contact_name: "", contact_email: "", contact_phone: "", city: "",
    teacher_count: 25, notes: "",
  });
  const [confirmedId, setConfirmedId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);

  useEffect(() => { api.get("/programs").then((r) => setPrograms(r.data)); }, []);
  useEffect(() => { api.get("/bookings/blocked-dates/public").then((r) => setBlockedDates(r.data || [])).catch(() => {}); }, []);

  const isDateBlocked = (dateStr) => blockedDates.includes((dateStr || "").slice(0, 10));

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const canNext = () =>
    (step === 0 && !!data.program_id) ||
    (step === 1 && !!data.preferred_date && !isDateBlocked(data.preferred_date)) ||
    (step === 2 && data.school_name && data.contact_name && data.contact_email && data.contact_phone) ||
    (step === 3 && data.teacher_count > 0) ||
    step === 4;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data: b } = await api.post("/bookings", data);
      setConfirmedId(b.id);
      setStep(5);
      toast.success("Booking submitted");
    } catch (e) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedProgram = programs.find((p) => p.id === data.program_id);

  if (step === 5) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-24 text-center fade-up">
        <div className="w-16 h-16 rounded-full bg-emerald-100 grid place-items-center mx-auto mb-6">
          <Check className="w-8 h-8 text-emerald-600" strokeWidth={2.5} />
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight">Booking received.</h1>
        <p className="mt-4 text-gray-600">We've emailed a confirmation to <b>{data.contact_email}</b>. Our team will call within 24 hours.</p>
        <div className="mt-6 inline-block px-4 py-2 rounded-lg bg-gray-100 text-sm font-mono">Ref · {confirmedId}</div>
        {whatsapp && (
          <div className="mt-8 inline-flex items-center gap-3 rounded-2xl border border-green-200 bg-green-50 px-5 py-4">
            <MessageCircle className="w-5 h-5 text-green-600 shrink-0" />
            <div className="text-left">
              <div className="text-sm font-semibold text-green-800">Questions? Chat with us</div>
              <a
                href={`https://wa.me/${whatsapp.replace(/[^\d]/g, "")}`}
                target="_blank"
                rel="noreferrer"
                data-testid="booking-whatsapp"
                className="text-sm text-green-700 hover:underline"
              >
                {whatsapp} via WhatsApp →
              </a>
            </div>
          </div>
        )}
        <div className="mt-10">
          <button onClick={() => nav("/")} data-testid="booking-home" className="text-sm font-medium text-[#4F46E5] hover:underline">Return home →</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
      <div className="fade-up">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4F46E5] mb-3">Book a training</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight font-semibold">Let's design your cohort.</h1>
        <p className="mt-3 text-gray-600">Five short steps. Confirmation in under 24 hours.</p>
      </div>

      {/* Stepper */}
      <div className="mt-10 flex items-center gap-2">
        {steps.map((label, i) => (
          <div key={label} className="flex-1 flex items-center gap-2">
            <div className={`h-1.5 rounded-full flex-1 transition-colors ${i <= step ? "bg-[#4F46E5]" : "bg-gray-200"}`} />
            {i === steps.length - 1 && <span className="text-xs text-gray-500 whitespace-nowrap">Step {step + 1}/{steps.length}</span>}
          </div>
        ))}
      </div>
      <div className="mt-3 text-sm text-gray-500">{steps[step]}</div>

      <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 fade-up">
        {step === 0 && (
          <div className="grid gap-3">
            <label className="text-sm font-medium">Select a program</label>
            <div className="grid gap-2 max-h-96 overflow-auto">
              {programs.map((p) => (
                <button key={p.id} onClick={() => set("program_id", p.id)} data-testid={`book-program-${p.id}`}
                  className={`text-left rounded-xl border p-4 transition-all ${data.program_id === p.id ? "border-[#4F46E5] ring-2 ring-[#EEF2FF]" : "border-gray-200 hover:border-gray-300"}`}>
                  <div className="text-xs text-[#4F46E5] uppercase tracking-wider font-semibold">{p.category}</div>
                  <div className="font-medium mt-0.5">{p.title}</div>
                  <div className="text-xs text-gray-500 mt-1">{p.duration_hours}h · {p.level}</div>
                </button>
              ))}
            </div>
          </div>
        )}
        {step === 1 && (
          <div className="grid gap-3">
            <label className="text-sm font-medium">Preferred date</label>
            <div className="relative">
              <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input type="date" value={data.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} data-testid="book-date"
                className="w-full pl-9 pr-3 py-3 rounded-lg border border-gray-200 focus:border-[#4F46E5] outline-none text-sm"
                style={{ borderColor: isDateBlocked(data.preferred_date) ? "#DC2626" : undefined }} />
            </div>
            {isDateBlocked(data.preferred_date)
              ? <div className="text-xs text-rose-600">This date is unavailable. Please choose another date.</div>
              : <div className="text-xs text-gray-500">We'll confirm final dates within 24 hours.</div>}
          </div>
        )}
        {step === 2 && (
          <div className="grid gap-3">
            <input placeholder="School name" value={data.school_name} onChange={(e) => set("school_name", e.target.value)} data-testid="book-school-name" className="w-full px-3 py-3 rounded-lg border border-gray-200 focus:border-[#4F46E5] outline-none text-sm" />
            <div className="grid sm:grid-cols-2 gap-3">
              <input placeholder="Contact name" value={data.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid="book-contact-name" className="w-full px-3 py-3 rounded-lg border border-gray-200 focus:border-[#4F46E5] outline-none text-sm" />
              <input placeholder="Contact phone" value={data.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid="book-contact-phone" className="w-full px-3 py-3 rounded-lg border border-gray-200 focus:border-[#4F46E5] outline-none text-sm" />
            </div>
            <input type="email" placeholder="Contact email" value={data.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="book-contact-email" className="w-full px-3 py-3 rounded-lg border border-gray-200 focus:border-[#4F46E5] outline-none text-sm" />
            <input placeholder="City" value={data.city} onChange={(e) => set("city", e.target.value)} data-testid="book-city" className="w-full px-3 py-3 rounded-lg border border-gray-200 focus:border-[#4F46E5] outline-none text-sm" />
          </div>
        )}
        {step === 3 && (
          <div className="grid gap-4">
            <label className="text-sm font-medium">Number of teachers</label>
            <div className="flex items-center gap-4">
              <input type="range" min="5" max="200" value={data.teacher_count} onChange={(e) => set("teacher_count", parseInt(e.target.value))} data-testid="book-teacher-count" className="flex-1 accent-[#4F46E5]" />
              <div className="w-16 text-center px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200 font-mono text-sm">{data.teacher_count}</div>
            </div>
            <textarea placeholder="Anything else we should know? (optional)" value={data.notes} onChange={(e) => set("notes", e.target.value)} data-testid="book-notes"
              className="w-full px-3 py-3 rounded-lg border border-gray-200 focus:border-[#4F46E5] outline-none text-sm min-h-24" />
          </div>
        )}
        {step === 4 && (
          <div className="grid gap-3 text-sm">
            <div className="flex items-center gap-2 text-[#4F46E5] font-medium"><Sparkles className="w-4 h-4" /> Review</div>
            <Row k="Program" v={selectedProgram?.title || "—"} />
            <Row k="Date" v={data.preferred_date} />
            <Row k="School" v={data.school_name} />
            <Row k="Contact" v={`${data.contact_name} · ${data.contact_email} · ${data.contact_phone}`} />
            <Row k="City" v={data.city || "—"} />
            <Row k="Teachers" v={<span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {data.teacher_count}</span>} />
            {data.notes && <Row k="Notes" v={data.notes} />}
          </div>
        )}

        <div className="mt-8 flex items-center justify-between">
          <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="book-back"
            className="text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-40 disabled:cursor-not-allowed">
            Back
          </button>
          {step < 4 ? (
            <button onClick={() => setStep((s) => s + 1)} disabled={!canNext()} data-testid="book-next"
              className="inline-flex items-center gap-1.5 bg-[#0F172A] hover:bg-[#1e293b] disabled:opacity-40 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-all">
              Continue <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={submit} disabled={submitting} data-testid="book-submit"
              className="inline-flex items-center gap-1.5 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-all">
              {submitting ? "Submitting…" : "Submit booking"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex items-start justify-between border-b border-gray-100 py-2">
    <div className="text-gray-500">{k}</div>
    <div className="font-medium text-right max-w-[65%]">{v}</div>
  </div>
);